// hotkeys/isTyping.ts
export const isEditableElement = (el: EventTarget | null): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  
  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (el.isContentEditable || el.contentEditable === "true") return true;
  
  // Check if element is inside MUI components that should be ignored
  // MUI Autocomplete, DatePicker, and other form controls
  if (el.closest('[role="combobox"]') || 
      el.closest('[role="textbox"]') || 
      el.closest('.MuiAutocomplete-input') ||
      el.closest('.MuiInput-input') ||
      el.closest('.MuiOutlinedInput-input') ||
      el.closest('.MuiFilledInput-input')) {
    return true;
  }
  
  // Check for contenteditable within the element tree
  let current: HTMLElement | null = el;
  while (current && current !== document.body) {
    if (current.isContentEditable) return true;
    current = current.parentElement;
  }
  
  return false;
};

export const isModalOrDialogOpen = (): boolean => {
  // console.debug("[isModalOrDialogOpen] Checking for open modals/dialogs...");
  
  // Check for MUI Dialog backdrop - but only if it's actually visible
  const muiBackdrop = document.querySelector('.MuiBackdrop-root');
  if (muiBackdrop) {
    const backdrop = muiBackdrop as HTMLElement;
    const computedStyle = window.getComputedStyle(backdrop);
    const isVisible = computedStyle.opacity !== "0" && 
                     computedStyle.visibility !== "hidden" && 
                     computedStyle.display !== "none" &&
                     backdrop.offsetParent !== null;
    
    // console.debug("[isModalOrDialogOpen] Found MUI backdrop:", {
      // element: backdrop,
      // opacity: computedStyle.opacity,
      // visibility: computedStyle.visibility,
      // display: computedStyle.display,
      // offsetParent: backdrop.offsetParent,
      // isVisible: isVisible,
      // parentId: backdrop.parentElement?.id,
      // parentClass: backdrop.parentElement?.className
    // });
    
    if (isVisible) {
      // console.debug("[isModalOrDialogOpen] Backdrop is visible - checking for associated dialog");
      
      // Try to identify what dialog is associated with this backdrop
      const parentDialog = backdrop.parentElement?.querySelector('[role="dialog"]');
      if (parentDialog) {
        const dialogClass = parentDialog.className;
        const dialogId = parentDialog.id;
        const dialogAriaLabel = parentDialog.getAttribute('aria-label');
        const dialogAriaLabelledBy = parentDialog.getAttribute('aria-labelledby');
        
        // console.debug("[isModalOrDialogOpen] Associated dialog found:", {
          // element: parentDialog,
          // className: dialogClass,
          // id: dialogId,
          // ariaLabel: dialogAriaLabel,
          // ariaLabelledBy: dialogAriaLabelledBy
        // });
        
        // Try to get the dialog title if aria-labelledby is present
        if (dialogAriaLabelledBy) {
          const titleElement = document.getElementById(dialogAriaLabelledBy);
          if (titleElement) {
            // console.debug("[isModalOrDialogOpen] Dialog title:", titleElement.textContent);
          }
        }
      }
      
      return true;
    } else {
      // console.debug("[isModalOrDialogOpen] Backdrop found but not visible - ignoring");
    }
  }
  
  // Check for any modal/dialog with common class names or attributes
  const modals = document.querySelectorAll(
    '[role="dialog"], [role="alertdialog"], .MuiDialog-root, .modal, [data-modal="true"]'
  );
  
  if (modals.length > 0) {
    // console.debug(`[isModalOrDialogOpen] Found ${modals.length} modal(s)/dialog(s):`);
    
    let visibleModals = 0;
    
    modals.forEach((modal, index) => {
      const element = modal as HTMLElement;
      const computedStyle = window.getComputedStyle(element);
      const isVisible = computedStyle.opacity !== "0" && 
                       computedStyle.visibility !== "hidden" && 
                       computedStyle.display !== "none" &&
                       element.offsetParent !== null;
      
      const info = {
        index,
        tagName: element.tagName,
        className: element.className,
        id: element.id,
        role: element.getAttribute('role'),
        ariaLabel: element.getAttribute('aria-label'),
        ariaLabelledBy: element.getAttribute('aria-labelledby'),
        dataModal: element.getAttribute('data-modal'),
        isVisible: isVisible,
        computedDisplay: computedStyle.display,
        computedVisibility: computedStyle.visibility,
        computedOpacity: computedStyle.opacity
      };
      
      // console.debug(`[isModalOrDialogOpen] Modal ${index}:`, info);
      
      if (isVisible) {
        visibleModals++;
        
        // Try to get title/content for identification
        if (info.ariaLabelledBy) {
          const titleElement = document.getElementById(info.ariaLabelledBy);
          if (titleElement) {
            // console.debug(`[isModalOrDialogOpen] Modal ${index} title:`, titleElement.textContent);
          }
        }
        
        // Look for common dialog title selectors
        const titleSelectors = [
          '.MuiDialogTitle-root',
          '[data-testid="dialog-title"]',
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6'
        ];
        
        for (const selector of titleSelectors) {
          const titleEl = element.querySelector(selector);
          if (titleEl) {
            // console.debug(`[isModalOrDialogOpen] Modal ${index} found title via ${selector}:`, titleEl.textContent);
            break;
          }
        }
      }
    });
    
    // console.debug(`[isModalOrDialogOpen] ${visibleModals} of ${modals.length} modals are actually visible`);
    return visibleModals > 0;
  }
  
  // console.debug("[isModalOrDialogOpen] No modals/dialogs detected");
  return false;
};

export const shouldIgnoreKeyEvent = (e: KeyboardEvent, suppressed: boolean): boolean => {
  const reasons = [];
  
  if (suppressed) {
    reasons.push("suppressed");
  }
  if (e.isComposing) {
    reasons.push("composing");
  }
  if (e.repeat) {
    reasons.push("repeat");
  }
  
  // Allow some modifier combinations but ignore most
  // We mainly want single keys, but allow shift for uppercase letters
  if (e.altKey || e.ctrlKey || e.metaKey) {
    reasons.push(`modifiers: alt=${e.altKey}, ctrl=${e.ctrlKey}, meta=${e.metaKey}`);
  }
  
  if (isEditableElement(e.target)) {
    reasons.push("editable element");
  }
  if (isModalOrDialogOpen()) {
    reasons.push("modal/dialog open");
  }
  
  const shouldIgnore = reasons.length > 0;
  
  if (shouldIgnore) {
    // console.debug(`[shouldIgnoreKeyEvent] Ignoring key "${e.key}" because:`, reasons);
  } else {
    // console.debug(`[shouldIgnoreKeyEvent] Allowing key "${e.key}"`);
  }
  
  return shouldIgnore;
};